/**
 * A wizard's steps and where the person is in them — the logic both kits'
 * `WizardDialog` draw from, so the two behave alike and differ only in looks.
 *
 * A step says what stops it with `blocker`: a short sentence ("Give it a
 * name.") shown beside Next, which stays off until it is `null`. Steps already
 * reached can be gone back to from the rail; the ones ahead open only by
 * moving through, so nothing a later step depends on is skipped.
 */

import { useEffect, useState, type ReactNode } from 'react';

export interface WizardStep {
  id: string;
  /** The rail's title, and its line beneath. */
  title: string;
  hint?: string;
  icon?: ReactNode;
  /** The step's own heading above its fields, and a line under it. */
  heading?: string;
  description?: ReactNode;
  /** The fields — or, given a way to open another step by its id, what is
   *  drawn from it (a review step's "Edit" beside each part). */
  content: ReactNode | ((openStep: (id: string) => void) => ReactNode);
  /** Why it cannot move on yet, in words; `null` (or left out) when it can. */
  blocker?: string | null;
}

export interface WizardState {
  index: number;
  /** The furthest step reached: every step up to it can be opened again. */
  furthest: number;
  current: WizardStep;
  isFirst: boolean;
  isLast: boolean;
  /** How far through, 0–100, by steps finished. */
  percent: number;
  canOpen: (index: number) => boolean;
  open: (index: number) => void;
  /** Open a step reached already, by its id. */
  openStep: (id: string) => void;
  next: () => void;
  back: () => void;
}

/**
 * ``reachable``: every step may be opened from the start — an edit of
 * something that already passed every step.
 */
export function useWizard(steps: WizardStep[], active: boolean, reachable = false): WizardState {
  const [index, setIndex] = useState(0);
  const [furthest, setFurthest] = useState(reachable ? steps.length - 1 : 0);

  // Each opening starts at the beginning.
  useEffect(() => {
    if (!active) return;
    setIndex(0);
    setFurthest(reachable ? steps.length - 1 : 0);
  }, [active, reachable, steps.length]);

  const last = steps.length - 1;
  const safe = Math.min(index, last);
  // A wizard has at least one step; the caller's list is never empty.
  const current = steps[safe] as WizardStep;
  const blockedBefore = (target: number) =>
    steps.slice(0, target).some((step) => step.blocker);

  const canOpen = (target: number) => target <= furthest && !blockedBefore(target);
  const open = (target: number) => {
    if (target >= 0 && target <= last && canOpen(target)) setIndex(target);
  };
  const openStep = (id: string) => open(steps.findIndex((step) => step.id === id));
  const next = () => {
    if (current.blocker || safe >= last) return;
    setIndex(safe + 1);
    setFurthest((reached) => Math.max(reached, safe + 1));
  };
  const back = () => setIndex(Math.max(0, safe - 1));

  return {
    index: safe,
    furthest,
    current,
    isFirst: safe === 0,
    isLast: safe === last,
    percent: Math.round((safe / Math.max(1, steps.length)) * 100),
    canOpen,
    open,
    openStep,
    next,
    back,
  };
}

/** A step's content, drawn: its fields, or what it draws given `openStep`. */
export function stepContent(step: WizardStep, openStep: (id: string) => void): ReactNode {
  return typeof step.content === 'function' ? step.content(openStep) : step.content;
}
